// src/SiteContext.tsx

import React, { createContext, useContext, useState, ReactNode, useEffect } from "react";
import { initialize as  APIUtilInitialize} from './api/apiUtils';
import { translator } from "./i18n/localization";
import { TimesheetType } from "./interfaces/TimesheetInterface";
import { useIsAuthenticated, useMsal } from "@azure/msal-react";
import { Page } from "./interfaces/PagesInterface";
import { PopupProps } from "./interfaces/Popup";
import {PayPeriod} from "./interfaces/PayPeriodInterface";
import {defaultEmployee, Employee} from "./interfaces/EmployeeInterface";
import {AdminEmployeeRole} from "./interfaces/EmployeeInfoInterface";
import {Location} from "./interfaces/LocationsInterface";
import {MiscTaskApprovalConfig} from "./interfaces/LayoutInterface";
import {DeepLinkParams} from "./interfaces/DeepLinkInterface";
import type {StatusKey} from "./Pages/EmployeeTimesheetReviewConsole";
import config from "./configLoader";
import { Experience, safeStorage, writeStoredExperience } from "./Pages/v2/employeePortal/portalSession";

/**
 * Home → Timesheet Review board handoff. HomeV2 sets this on a stat-card /
 * search-result click; the v2 board reads it once buckets load to preselect a
 * bucket (and optionally open an employee's review queue), then clears it.
 */
export interface ReviewIntent {
  bucket?: StatusKey;
  employeeID?: number;
  /** Carried from an employee search so the board can open the person even if
   *  they aren't present in the mapped bucket's supervisor list. */
  altEmployeeID?: string;
  name?: { first: string; middle?: string; last: string };
}

/**
 * Project Map → Projects page handoff ("Open in Projects" on a map popup).
 * The map sets this then navigates to Page.Projects; the Projects page (v1 & v2)
 * consumes it on mount — looks the project up and opens it — then clears it.
 */
export interface ProjectOpenIntent {
  projectID: number;
  /** Used for the lookup (typeahead/searchAll key). */
  altProjectID: string;
}

/**
 * Board/console → Payroll Timesheet Edit page handoff (M2-B). "Edit as payroll"
 * sets this then navigates to Page.PayrollTimesheetEdit; the editor consumes it
 * on mount (pay period comes from the shared payPeriodID context). It is NOT
 * cleared on read — the page must survive a remount (reloadNonce) mid-edit.
 */
export interface PayrollEditIntent {
  employeeID: number;
  altEmployeeID?: string;
  name?: { first: string; middle?: string; last: string };
}

export const defaultAdminEmployeeRole: AdminEmployeeRole = {
  adminEmployeeRoleID: 0,
  name: '',
  admin: false,
  supervisor: false,
  payroll: false,
  active: false,
  hr: false,
  editEmployeeRole: false,
  viewProjectMap: false,
  editProjectMap: false,
  editMiscTaskAccountCodeAndDescription: false,
  viewAllProjectsMap: false,
  importPieces: false,
  viewEmployeeEnrollmentPhotos: false,
  editEmployeeEnrollmentPhotos: false,
  activateInactivateEmployees: false,
  manageEmployeeRoles: false,
};


// Define the context's type
interface SiteContextProps {
  userRole: AdminEmployeeRole;
  setUserRole: (role: AdminEmployeeRole) => void;
  error: string | null;
  setError: (error: string | null) => void;
  userID: number;
  setUserID: (id: number) => void;
  /** The signed-in user's own EmployeeID when they have employee (time portal)
   *  access, from the login response's selfEmployee (TAS-667). Null otherwise.
   *  Consumed by the Phase 3 employee portal. */
  selfEmployeeID: number | null;
  setSelfEmployeeID: (id: number | null) => void;
  supervisorQueryID: number;
  setSupervisorQueryID: (id: number) => void;
  userName: string;
  setUserName: (name: string) => void;
  loading: boolean;
  setLoading: (loading: boolean) => void;
  week: number;
  setWeek: (week: number) => void;
  payPeriods: PayPeriod[];
  setPayPeriods: (payPeriods: PayPeriod[]) => void;
  payPeriodID: number;
  setPayPeriodID: (payPeriodID: number) => void;
  year: string;
  setYear: (year: string) => void;
  startDate: string;
  setStartDate: (startDate: string) => void;
  endDate: string;
  setEndDate: (endDate: string) => void;
  superID: number;
  setSuperID: (superID: number) => void;
  timesheetID: any;
  setTimesheetID: (timesheetID: any) => void;
  superName: string;
  setSuperName: (superName: string) => void;
  type: TimesheetType;
  setType: (type: TimesheetType) => void;
  page: Page;
  setPage: (page: Page) => void;
  history: Page[]; // Track the history of pages
  setHistory: (history: Page[]) => void; // Set the history stack
  goBack: () => void; // Function to go back to the previous page
  popupProps: PopupProps;
  setPopupProps: (newPopupProps: PopupProps) => void;
  isModalVisible: boolean;
  setIsModalVisible: (isModalVisible: boolean) => void;
  createPopup: (title: string, message: string) => void;
  translator: typeof translator;
  superAltID: string;
  setSuperAltID: (superID: string) => void;
  employee: Employee;
  setEmployee: (employee: Employee) => void;
  locations: Location[];
  setLocations: (locations: Location[]) => void;
  miscTaskApprovalConfigs: MiscTaskApprovalConfig[];
  setMiscTaskApprovalConfigs: (configs: MiscTaskApprovalConfig[]) => void;
  pendingDeepLink: DeepLinkParams | null;
  setPendingDeepLink: (params: DeepLinkParams | null) => void;
  pendingTimeOffCount: number;
  setPendingTimeOffCount: (count: number) => void;
  /** Incremented to force the active page to remount (e.g. clicking the nav item
   *  for the page you're already on, which otherwise is a no-op). */
  reloadNonce: number;
  bumpReload: () => void;
  reviewIntent: ReviewIntent | null;
  setReviewIntent: (intent: ReviewIntent | null) => void;
  projectIntent: ProjectOpenIntent | null;
  setProjectIntent: (intent: ProjectOpenIntent | null) => void;
  payrollEditIntent: PayrollEditIntent | null;
  setPayrollEditIntent: (intent: PayrollEditIntent | null) => void;
  /** Employee Time Portal (P-TAS-20 Phase 3). The signed-in user has an admin role (the
   *  admin shell may mount) / an employee role (POST /admin/login employeeAccess). Both are
   *  stored for every login, so a user with both roles keeps their employee access. */
  hasAdminAccess: boolean;
  setHasAdminAccess: (v: boolean) => void;
  hasEmployeeAccess: boolean;
  setHasEmployeeAccess: (v: boolean) => void;
  /** Which experience the shell shows (see portalSession.resolveExperience). */
  experience: Experience;
  /** Sets the experience; `persist` (default true) remembers it for this user (localStorage). */
  setExperience: (experience: Experience, persist?: boolean) => void;
  /** experience === 'employee' on a tenant whose portal is enabled, for a user with employee access. */
  employeeMode: boolean;
  /** Sidebar badges in employee mode: last week not submitted (amber "1"), pending time off. */
  portalBadges: { timesheet: number; timeOff: number };
  setPortalBadges: (badges: { timesheet: number; timeOff: number }) => void;
  /** The signed-in user's own Role name (login selfEmployee.roleName), for the user menu. */
  selfRoleName: string | null;
  setSelfRoleName: (name: string | null) => void;
}

const SiteContext = createContext<SiteContextProps>({
  userRole: defaultAdminEmployeeRole,
  setUserRole: () => {},
  error: null,
  setError: () => {},
  userID: 0,
  setUserID: () => {},
  selfEmployeeID: null,
  setSelfEmployeeID: () => {},
  supervisorQueryID: 0,
  setSupervisorQueryID: () => {},
  userName: '',
  setUserName: () => {},
  loading: true,
  setLoading: () => {},
  week: 0,
  setWeek: () => {},
  payPeriods: [],
  setPayPeriods: () => {},
  payPeriodID: 0,
  setPayPeriodID: () => {},
  year: '',
  setYear: () => {},
  startDate: '',
  setStartDate: () => {},
  endDate: '',
  setEndDate: () => {},
  superID: 0,
  setSuperID: () => {},
  timesheetID: 0,
  setTimesheetID: () => {},
  superName: '',
  setSuperName: () => {},
  type: TimesheetType.Unsubmitted,
  setType: () => {},
  page: Page.Dashboard,
  setPage: () => {},
  history: [],
  setHistory: () => {},
  goBack: () => {},
  isModalVisible: false,
  setIsModalVisible: () => {},
  popupProps: {
    isModalVisible: false,
    closeModal: () => {},
    popupTitle: '',
    popupMessage: ''
  },
  setPopupProps: () => {},
  createPopup: () => {},
  translator,
  superAltID: '',
  setSuperAltID: () => {},
    employee: defaultEmployee,
    setEmployee: () => {},
    locations: [],
    setLocations: () => {},
    miscTaskApprovalConfigs: [] as MiscTaskApprovalConfig[],
    setMiscTaskApprovalConfigs: () => {},
    pendingDeepLink: null,
    setPendingDeepLink: () => {},
    pendingTimeOffCount: 0,
    setPendingTimeOffCount: () => {},
    reloadNonce: 0,
    bumpReload: () => {},
    reviewIntent: null,
    setReviewIntent: () => {},
    projectIntent: null,
    setProjectIntent: () => {},
    payrollEditIntent: null,
    setPayrollEditIntent: () => {},
    hasAdminAccess: false,
    setHasAdminAccess: () => {},
    hasEmployeeAccess: false,
    setHasEmployeeAccess: () => {},
    experience: 'admin',
    setExperience: () => {},
    employeeMode: false,
    portalBadges: { timesheet: 0, timeOff: 0 },
    setPortalBadges: () => {},
    selfRoleName: null,
    setSelfRoleName: () => {},
});

// Create the custom hook
export const useSiteContext = (): SiteContextProps => {
  return useContext(SiteContext);
};

interface SiteProviderProps {
  children: ReactNode;
}

// Create the SiteContext provider
export const SiteContextProvider: React.FC<SiteProviderProps> = ({ children }) => {
  const [userRole, setUserRole] = useState<AdminEmployeeRole>(defaultAdminEmployeeRole);
  const [error, setError] = useState<string | null>(null);
  const [userID, setUserID] = useState<number>(0);
  const [selfEmployeeID, setSelfEmployeeID] = useState<number | null>(null);
  const [supervisorQueryID, setSupervisorQueryID] = useState<number>(0);
  const [userName, setUserName] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(true);
  const [week, setWeek] = useState<number>(0);
  const [payPeriods, setPayPeriods] = useState<PayPeriod[]>([]);
  const [payPeriodID, setPayPeriodID] = useState<number>(0);
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');
  const [year, setYear] = useState<string>('');
  const [empID, setEmpID] = useState<number>(0);
  const [superID, setSuperID] = useState<number>(0);
  const [superAltID, setSuperAltID] = useState<string>('');
  const [superName, setSuperName] = useState<string>('');
  const [timesheetID, setTimesheetID] = useState<any>();
  const [type, setType] = useState<TimesheetType>(TimesheetType.Unsubmitted);
  const [page, setPage] = useState<Page>(Page.Dashboard);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [employee, setEmployee] = useState<Employee>(defaultEmployee);
  const [locations, setLocations] = useState<Location[]>([]);
  const [miscTaskApprovalConfigs, setMiscTaskApprovalConfigs] = useState<MiscTaskApprovalConfig[]>([]);
  const [pendingDeepLink, setPendingDeepLink] = useState<DeepLinkParams | null>(null);
  const [reloadNonce, setReloadNonce] = useState<number>(0);
  const bumpReload = () => setReloadNonce((n) => n + 1);
  const [pendingTimeOffCount, setPendingTimeOffCount] = useState<number>(0);
  const [reviewIntent, setReviewIntent] = useState<ReviewIntent | null>(null);
  const [projectIntent, setProjectIntent] = useState<ProjectOpenIntent | null>(null);
  const [payrollEditIntent, setPayrollEditIntent] = useState<PayrollEditIntent | null>(null);
  const [hasAdminAccess, setHasAdminAccess] = useState<boolean>(false);
  const [hasEmployeeAccess, setHasEmployeeAccess] = useState<boolean>(false);
  const [experience, setExperienceState] = useState<Experience>('admin');
  const [selfRoleName, setSelfRoleName] = useState<string | null>(null);
  const [portalBadges, setPortalBadges] = useState<{ timesheet: number; timeOff: number }>({ timesheet: 0, timeOff: 0 });
  const setExperience = (next: Experience, persist: boolean = true) => {
    setExperienceState(next);
    if (persist) writeStoredExperience(safeStorage('local'), userID, next);
  };
  const employeeMode = !!config.employeePortal?.enabled && hasEmployeeAccess && experience === 'employee';
  const [popupProps, setPopupProps] = useState<PopupProps>({
    isModalVisible: false,
    closeModal: () => setIsModalVisible(false),  // Define a function to close the modal
    popupTitle: '',
    popupMessage: '',
  });

  const isAuthenticated = useIsAuthenticated();

  // Track page history
  const [history, setHistory] = useState<Page[]>([]);

  // Function to go back to the previous page
  const goBack = () => {
    setHistory((prevHistory) => {
      // Remove the current page if it's at the end of the history
      const newHistory = prevHistory.slice(0, prevHistory.length - 1);

      // Find the previous page that is not the same as the current page
      const previousPage = newHistory.reverse().find((p) => p !== page) || Page.Dashboard;

      // Set the previous valid page as the current page
      setPage(previousPage);
      return newHistory.reverse(); // return the updated history
    });
  };

  const createPopup = (title: string, message: string) => {
    setPopupProps({
      isModalVisible: true,  // Show the modal
      closeModal: () => setPopupProps(prevState => ({
        ...prevState,
        isModalVisible: false,  // Close the modal by updating `isModalVisible`
      })), // Function to close the modal
      popupTitle: title,     // Set the popup title
      popupMessage: message  // Set the popup message
    });
  };

  // Update history when the page changes, ensuring no duplicates
  useEffect(() => {
    setHistory((prevHistory) => {
      // Only add the page if it's not the same as the last page in history
      if (prevHistory[prevHistory.length - 1] !== page) {
        return [...prevHistory, page];
      }
      return prevHistory; // Prevent duplicates
    });
  }, [page]);

  useEffect(() => {
    APIUtilInitialize(createAlert);
  }, []);
  
  const createAlert = (title: string, message: string) => createPopup(title, message);

  return (
    <SiteContext.Provider
      value={{
        userRole,
        setUserRole,
        error,
        setError,
        userID,
        setUserID,
        selfEmployeeID,
        setSelfEmployeeID,
        supervisorQueryID,
        setSupervisorQueryID,
        userName,
        setUserName,
        loading,
        setLoading,
        week,
        setWeek,
        payPeriods,
        setPayPeriods,
        payPeriodID,
        setPayPeriodID,
        startDate,
        setStartDate,
        endDate,
        setEndDate,
        year,
        setYear,
        timesheetID,
        setTimesheetID,
        superID,
        setSuperID,
        superName,
        setSuperName,
        type,
        setType,
        page,
        setPage,
        history,
        setHistory,
        goBack, 
        isModalVisible,
        setIsModalVisible,
        createPopup,
        translator,
        popupProps,
        setPopupProps,
        superAltID,
        setSuperAltID,
        employee,
        setEmployee,
        locations,
        setLocations,
        miscTaskApprovalConfigs,
        setMiscTaskApprovalConfigs,
        pendingDeepLink,
        setPendingDeepLink,
        pendingTimeOffCount,
        setPendingTimeOffCount,
        reloadNonce,
        bumpReload,
        reviewIntent,
        setReviewIntent,
        projectIntent,
        setProjectIntent,
        payrollEditIntent,
        setPayrollEditIntent,
        hasAdminAccess,
        setHasAdminAccess,
        hasEmployeeAccess,
        setHasEmployeeAccess,
        experience,
        setExperience,
        employeeMode,
        portalBadges,
        setPortalBadges,
        selfRoleName,
        setSelfRoleName,
      }}
    >
      {children}
    </SiteContext.Provider>
  );
};
